import { mutationOptions } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import { Logger } from '@/utils/logging';

import type {
  DismissNotificationParams,
  DismissNotificationResponse,
  DismissNotificationResult,
} from './types';

/**
 * Dismiss one or more notifications so they are no longer returned by the
 * pending-notifications query (permanent server-side suppression).
 *
 * @param params - The notification ids to dismiss
 * @returns The dismiss result for each id
 */
const dismissNotification = async ({
  ids,
}: DismissNotificationParams): Promise<DismissNotificationResponse> => {
  try {
    return await Promise.all(
      ids.map(async (id) => {
        const { data } = await apiClient.put<DismissNotificationResult>(
          `/v1/notifications/${id}/dismiss`,
        );
        return data;
      }),
    );
  } catch (error) {
    Logger.error('Error dismissing notifications', { error });
    throw error;
  }
};

export const dismissNotificationMutation = (
  options?: UseMutationOptions<
    DismissNotificationResponse,
    Error,
    DismissNotificationParams
  >,
) =>
  mutationOptions({
    mutationFn: (params: DismissNotificationParams) =>
      dismissNotification(params),
    mutationKey: ['dismissNotification'],
    retry: false,
    ...options,
  });
